import React from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { PipelineStep } from '../utils/pipelineSteps';

interface PipelineFlowProps {
  steps: PipelineStep[];
  currentStepIndex: number;
  className?: string;
}

interface ServiceNodeProps {
  step: PipelineStep;
  isActive: boolean;
  isCompleted: boolean;
  index: number;
  isLast: boolean;
}

const ServiceNode: React.FC<ServiceNodeProps> = ({ step, isActive, isCompleted, index, isLast }) => {
  const Icon = step.icon;
  
  return (
    <div className="flex items-center">
      {/* Node */}
      <div className="relative flex flex-col items-center">
        {/* Node circle */}
        <div
          className={`
            relative w-16 h-16 rounded-full flex items-center justify-center
            transition-all duration-300
            ${isCompleted 
              ? 'bg-green-500 shadow-lg shadow-green-500/50' 
              : isActive 
              ? 'bg-primary shadow-lg shadow-primary/50 animate-pulse-scale' 
              : 'bg-muted'
            }
          `}
        >
          {isCompleted ? (
            <CheckCircleIcon className="w-8 h-8 text-white" />
          ) : (
            <Icon className={`w-8 h-8 ${isActive ? 'text-white' : 'text-muted-foreground'}`} />
          )}
        </div>
        
        {/* Label */}
        <div className="mt-2 text-center max-w-[120px]">
          <p className={`text-xs font-medium ${isActive ? 'text-foreground' : 'text-muted-foreground'} transition-colors`}>
            {step.label}
          </p>
        </div>
        
        {/* Service badge */}
        <div className={`mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
          isActive 
            ? 'bg-primary text-white' 
            : 'bg-muted text-muted-foreground'
        } transition-colors`}>
          {step.service.toUpperCase()}
        </div>
      </div>
      
      {/* Connection line */}
      {!isLast && (
        <div className="relative flex-1 h-0.5 mx-2">
          {/* Base line */}
          <div className={`absolute inset-0 ${
            isCompleted 
              ? 'bg-primary' 
              : isActive 
              ? 'bg-primary' 
              : 'bg-border'
          } transition-colors`} />
          
          {/* Animated particles */}
          {isActive && (
            <>
              <div 
                className="absolute top-1/2 w-2 h-2 -translate-y-1/2 rounded-full bg-primary shadow-lg shadow-primary/50 pipeline-particle"
                style={{ animationDelay: '0s' }}
              />
              <div 
                className="absolute top-1/2 w-2 h-2 -translate-y-1/2 rounded-full bg-primary shadow-lg shadow-primary/50 pipeline-particle"
                style={{ animationDelay: '1s' }}
              />
              <div 
                className="absolute top-1/2 w-2 h-2 -translate-y-1/2 rounded-full bg-primary shadow-lg shadow-primary/50 pipeline-particle"
                style={{ animationDelay: '2s' }}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

const PipelineFlow: React.FC<PipelineFlowProps> = ({ steps, currentStepIndex, className }) => {
  return (
    <div className={`w-full ${className}`}>
      <div className="flex items-start justify-between px-4 py-4 gap-2">
        {steps.map((step, index) => (
          <ServiceNode
            key={step.id}
            step={step}
            isActive={index === currentStepIndex}
            isCompleted={index < currentStepIndex}
            index={index}
            isLast={index === steps.length - 1}
          />
        ))}
      </div>
    </div>
  );
};

export default PipelineFlow;
